import { ActionIcon } from "@mantine/core";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MdModeEditOutline } from "react-icons/md";
import AddressForm from "../../common/AddressForm";
import { IAddress } from "../../../interfaces";
import CommonAddressCard from "./CommonAddressCard";
import Modal from "../../modal/Modal";
import { Auth } from "aws-amplify";
import { Logger } from "@aws-amplify/core";
import { toast } from "react-toastify";

const logger = new Logger("EditOrderAddress");

interface CognitoError extends Error {
  message: string;
}

const EditOrderAddress = ({
  label,
  address,
  onAddressChange,
  isEditable = false,
  orderEmail,
  onEmailChange,
}: {
  label: string;
  address: IAddress;
  onAddressChange: (address: IAddress, email?: string) => void;
  isEditable?: boolean;
  orderEmail?: string;
  onEmailChange?: (email: string) => void;
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const { t } = useTranslation();


  const handleSubmit = async (formData: IAddress, updatedEmail?: string) => {
    try {
      if (onEmailChange && updatedEmail && updatedEmail !== orderEmail) {
        console.log("🔄 Starting email update process:", {
          from: orderEmail,
          to: updatedEmail,
        });

        try {
          // Get current authenticated user
          const currentUser = await Auth.currentAuthenticatedUser();

          // Update Cognito email
          await Auth.updateUserAttributes(currentUser, {
            email: updatedEmail,
          });
          console.log("✅ Cognito email updated:", updatedEmail);

          // Update order email
          onEmailChange(updatedEmail);
          console.log("📦 Order email update triggered:", updatedEmail);
        } catch (emailError) {
          console.error("❌ Email update failed:", emailError);
          throw emailError;
        }
      }

      // Update address
      if (formData) {
        await onAddressChange(formData, updatedEmail);
        console.log("📝 Address updated with email:", updatedEmail);
      }

      closeEditModal();
    } catch (error) {
      console.error("❌ Update process failed:", error);
      const err = error as CognitoError;
      toast.error(
        err.message.includes("Cognito")
          ? t("errorUpdatingEmail")
          : t("errorUpdatingAddress")
      );
    }
  };

  const openEditModal = () => {
    const logContext = {
      component: "EditOrderAddress",
      method: "openEditModal",
      currentState: {
        isModalOpen: isEditModalOpen,
        currentEmail: orderEmail,
        currentAddress: address,
      },
    };

    console.log("Opening modal with email:", orderEmail);
    logger.info("Opening edit address modal", logContext);
    logger.debug("Current form state before opening", {
      ...logContext,
      formDetails: {
        address: {
          street: address.street,
          city: address.city,
          zipCode: address.zipCode,
          country: address.country,
          companyName: address.companyName,
        },
        email: orderEmail,
      },
    });

    setIsEditModalOpen(true);

    logger.info("Edit address modal opened successfully", {
      ...logContext,
      newState: { isModalOpen: true },
    });
  };

  const closeEditModal = () => {
    const logContext = {
      component: "EditOrderAddress",
      method: "closeEditModal",
      currentState: {
        isModalOpen: isEditModalOpen,
      },
    };

    logger.info("Closing edit address modal", logContext);
    setIsEditModalOpen(false);

    logger.info("Edit address modal closed successfully", {
      ...logContext,
      newState: { isModalOpen: false },
    });
  };

  return (
    <div className="address-details__address box-sizing-bb">
      <p className="text-sm font-bold row row-v-center gap-2">
        {t(label)}
        {isEditable && (
          <ActionIcon
            onClick={() => {
              console.log("Edit icon clicked, current email:", orderEmail);
              logger.debug("Edit address icon clicked", {
                component: "EditOrderAddress",
                method: "editIconClick",
                currentEmail: orderEmail,
              });
              openEditModal();
            }}
          >
            <MdModeEditOutline size="1rem" />
          </ActionIcon>
        )}
      </p>
      <CommonAddressCard address={address} orderEmail={orderEmail} />
      <Modal
        title={t("Edit Address")}
        visible={isEditModalOpen}
        onClose={() => {
          console.log("Modal closing, email was:", orderEmail);
          logger.debug("Modal close triggered", {
            component: "EditOrderAddress",
            method: "modalClose",
            trigger: "closeButton",
            currentEmail: orderEmail,
          });
          closeEditModal();
        }}
        compact
      >
        <AddressForm
          className="space-y-4"
          address={address}
          orderEmail={orderEmail}
          onCancel={() => {
            console.log("Form cancel, email was:", orderEmail);
            logger.debug("Address form cancel triggered", {
              component: "EditOrderAddress",
              method: "formCancel",
              currentState: {
                address,
                orderEmail,
              },
            });
            closeEditModal();
          }}
          onSubmit={(formData: IAddress, updatedEmail?: string) => {
            console.log("Form submission:", {
              formData,
              updatedEmail,
              originalEmail: orderEmail,
            });
            logger.debug("Address form submission triggered", {
              component: "EditOrderAddress",
              method: "formSubmit",
              submissionData: {
                address: {
                  street: formData.street,
                  city: formData.city,
                  zipCode: formData.zipCode,
                  country: formData.country,
                  companyName: formData.companyName,
                },
                emailUpdate: {
                  current: orderEmail,
                  new: updatedEmail,
                  isChanged: updatedEmail !== orderEmail,
                },
              },
            });
            handleSubmit(formData, updatedEmail);
          }}
        />
      </Modal>
    </div>
  );
};

export default EditOrderAddress;
